<template>
  <div class="flex gap-2 pt-4 pb-8">
    <div>
      <DropDownSelect :disabled="filtersDisabled?.timeframe">
        <template #activator="{ open, showDropDown }">
          <button
            class="flex items-center gap-1 rounded-sm border border-solid p-2 text-body-s"
            :class="[
              queryParams.timeframe !== TimeFrameOptionsEnum.NONE || showDropDown ? 'filter-selected' : 'filter-default',
              filtersDisabled?.timeframe ? '!border-disabled !text-disabled' : '',
            ]"
            data-testid="reviews-filter-timeframe-button"
            @click.prevent="open"
          >
            {{ t('Reviews.product.view.filters.timeframe.filtername') }}
            <ChevronDownIcon class="lucide-icon-m" />
          </button>
        </template>
        <template #default="{ close }">
          <ul class="options-list m-0 w-80 list-none p-0">
            <div class="flex items-center justify-between bg-surface-elevation-4 px-3 py-2 txt-body-s-bold">
              {{ t('Reviews.product.view.filters.timeframe.headline') }}
              <XIcon class="lucide-icon-s cursor-pointer" @click.prevent="close" />
            </div>
            <li v-for="option in timeframeOptions" :key="option.value">
              <label class="peer block cursor-pointer">
                <input
                  v-model="queryParams.timeframe"
                  type="radio"
                  :value="option.value"
                  name="timeframe-option"
                  class="peer pointer-events-none absolute hidden"
                  @change="handleDropDownFilterChange('timeframe', $event)"
                />
                <span class="block px-3 py-2 peer-checked:hidden hover:bg-surface-elevation-2">
                  {{ option.text }}
                </span>
                <span class="hidden items-center px-3 py-2 peer-checked:flex hover:bg-surface-elevation-2">
                  <span class="text-selected">{{ option.text }}</span>
                  <CheckIcon class="ml-4 lucide-icon-s" />
                </span>
              </label>
            </li>
          </ul>
        </template>
      </DropDownSelect>
    </div>
    <div>
      <ReviewsFilterMultiSelect
        v-if="purposesOptions"
        v-model="queryParams.purposes"
        :disabled="filtersDisabled?.purposes"
        :options="purposesOptions"
        :no-options-label="t('Reviews.product.view.filters.useCase.filterEmptyLabel')"
        :label="t('Reviews.product.view.filters.useCase.filtername')"
        data-testid="reviews-filter-use-case"
        @update:model-value="(v) => handleMultiSelectFilterChange('use_case', v)"
      >
        <template #header="{ close }">
          <div class="flex min-w-52 items-center justify-between bg-surface-elevation-4 px-2 py-2 txt-body-s-bold">
            {{ t('Reviews.product.view.filters.useCase.headline') }}
            <XIcon class="lucide-icon-s cursor-pointer" @click.prevent="close" />
          </div>
        </template>
      </ReviewsFilterMultiSelect>
    </div>
    <div>
      <ReviewsFilterMultiSelect
        v-model="queryParams.ratings"
        :disabled="filtersDisabled?.ratings"
        :options="ratingOptions"
        :label="t('Reviews.product.view.filters.rating.filtername')"
        data-testid="reviews-filter-rating"
        @update:model-value="(v) => handleMultiSelectFilterChange('rating', v)"
      >
        <template #header="{ close }">
          <div class="flex w-auto items-center justify-between bg-surface-elevation-4 px-2 py-2 txt-body-s-bold">
            {{ t('Reviews.product.view.filters.rating.headline') }}
            <XIcon class="lucide-icon-s cursor-pointer" @click.prevent="close" />
          </div>
        </template>
      </ReviewsFilterMultiSelect>
    </div>
    <div>
      <ReviewsFilterMultiSelect
        v-model="queryParams.states"
        :disabled="filtersDisabled?.states"
        :options="stateOptions"
        :label="t('Reviews.product.view.filters.state.filtername')"
        data-testid="reviews-filter-state"
        @update:model-value="(v) => handleMultiSelectFilterChange('state', v)"
      >
        <template #header="{ close }">
          <div class="flex w-48 items-center justify-between bg-surface-elevation-4 px-2 py-2 txt-body-s-bold">
            {{ t('Reviews.product.view.filters.state.headline') }}
            <XIcon class="lucide-icon-s cursor-pointer" @click.prevent="close" />
          </div>
        </template>
      </ReviewsFilterMultiSelect>
    </div>
    <div>
      <DropDownSelect :disabled="filtersDisabled?.interaction">
        <template #activator="{ open, showDropDown }">
          <button
            class="flex items-center gap-1 rounded-sm border border-solid p-2 text-body-s"
            :class="[
              queryParams.timeframe !== TimeFrameOptionsEnum.NONE || showDropDown ? 'filter-selected' : 'filter-default',
              filtersDisabled?.interaction ? '!border-disabled !text-disabled' : '',
            ]"
            data-testid="reviews-filter-interaction-button"
            @click.prevent="open"
          >
            {{ t('Reviews.product.view.filters.interactionOptions.filtername') }}
            <ChevronDownIcon class="lucide-icon-m" />
          </button>
        </template>
        <template #default="{ close }">
          <ul class="options-list m-0 w-96 list-none p-0">
            <div class="flex items-center justify-between bg-surface-elevation-4 px-3 py-2 txt-body-s-bold">
              {{ t('Reviews.product.view.filters.interactionOptions.headline') }}
              <XIcon class="lucide-icon-s cursor-pointer" @click.prevent="close" />
            </div>
            <li v-for="option in interactionOptions" :key="option.value">
              <label class="peer block cursor-pointer">
                <input
                  v-model="queryParams.interaction"
                  type="radio"
                  :value="option.value"
                  name="interaction-status"
                  class="peer pointer-events-none absolute hidden"
                  @change="handleDropDownFilterChange('interaction', $event)"
                />
                <span class="hidden h-full w-full items-center px-3 py-2 peer-checked:flex hover:bg-surface-elevation-2">
                  <span
                    :class="{
                      'pr-2': option.interactionState,
                      'pr-9': !option.interactionState,
                    }"
                    ><ReviewInteractionStatusIcon :type="option.interactionState || 'null'"
                  /></span>
                  <span class="text-selected">{{ option.text }}</span>
                  <CheckIcon class="ml-4 lucide-icon-s" />
                </span>
                <span class="flex h-full w-full items-center px-3 py-2 peer-checked:hidden hover:bg-surface-elevation-2">
                  <span
                    :class="{
                      'pr-2': option.interactionState,
                      'pr-9': !option.interactionState,
                    }"
                    ><ReviewInteractionStatusIcon :type="option.interactionState || 'null'"
                  /></span>
                  <span>{{ option.text }}</span>
                </span>
              </label>
            </li>
          </ul>
        </template>
      </DropDownSelect>
    </div>
    <button
      class="txt-body-s-bold"
      :class="hasNoActiveFilters ? 'hidden' : ''"
      :disabled="hasNoActiveFilters"
      data-testid="reviews-filter-clear"
      @click="handleResetFilters()"
    >
      <span class="inline-flex items-center gap-1">
        <XIcon class="lucide-icon-s" />
        {{ t('Reviews.product.view.filters.clearAllButton') }}
      </span>
    </button>
  </div>
</template>
<script setup lang="ts">
import { ChevronDownIcon, XIcon, CheckIcon } from '@ramp106/omrjs-core-ui/icons'
import { track } from '@ramp106/omrjs-tracking-reviews'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import { ReviewInteractionType } from '@/components/ReviewsCommon/ReviewInteractionStatus/types'
import { useRouterQueryFilters } from '@/components/ReviewsManagement/Reviews/useRouterQueryReviewsFilters'
import { SurveyResponseState } from '@/composables/useSurveyResponseState'
import { RecommendationRatingKeyEnum, SurveyResponsesStateEnum, SurveyResponsesViewEnum, type KeywordFacet } from '@/gql/reviews'

import ReviewsFilterMultiSelect from './ReviewsFilterMultiSelect.vue'
import { TimeFrameOptionsEnum, TIMEFRAME_FILTER_GTM_VALUES, type reviewsFilterMultiSelectOption } from './types'

export interface FiltersDisabledState {
  timeframe?: boolean
  purposes?: boolean
  ratings?: boolean
  states?: boolean
  interaction?: boolean
}

const props = defineProps<{
  purposes?: KeywordFacet[]
  filtersDisabled?: FiltersDisabledState
}>()

const { t } = useI18n()

const { queryParams, hasNoActiveFilters, setReviewFilters, resetAllFilters } = useRouterQueryFilters()

const filterGroup = computed(() =>
  queryParams.value.view === SurveyResponsesViewEnum.Outdated ? 'manage_tool_reviews_outdated' : 'manage_tool_reviews_all',
)

const INTERACTION_FILTER_GTM_VALUES: Record<ReviewInteractionType, string> = {
  [ReviewInteractionType.NONE]: 'none',
  [ReviewInteractionType.REMINDER_CHECK]: 'with_reminders',
  [ReviewInteractionType.REMINDER]: 'without_reminders',
  [ReviewInteractionType.REPLY_CHECK]: 'with_replies',
  [ReviewInteractionType.REPLY]: 'without_replies',
  [ReviewInteractionType.REPLY_OUTDATED]: 'with_outdated_replies',
}

function handleMultiSelectFilterChange(name: 'use_case' | 'rating' | 'state', value: string[]) {
  track('filter_apply', {
    surface: 'filter',
    filter_group: filterGroup.value,
    filter_type: 'tickbox',
    filter_name: name,
    filter_value: value.join(','),
  })
  setReviewFilters(true)
}

function handleDropDownFilterChange(name: 'timeframe' | 'interaction', event: Event) {
  const value = (event.target as HTMLInputElement).value
  // Radio values come from option.value, which is always a valid enum member.
  const gtmValue =
    name === 'timeframe'
      ? TIMEFRAME_FILTER_GTM_VALUES[value as TimeFrameOptionsEnum]
      : INTERACTION_FILTER_GTM_VALUES[value as ReviewInteractionType]
  track('filter_apply', {
    surface: 'filter',
    filter_group: filterGroup.value,
    filter_type: 'dropdown',
    filter_name: name,
    filter_value: gtmValue,
  })
  setReviewFilters(true)
}

function handleResetFilters() {
  track('filter_apply', {
    surface: 'filter',
    filter_group: filterGroup.value,
    filter_type: 'button',
    filter_name: 'reset_filter',
    filter_value: '',
  })
  resetAllFilters()
}

const timeframeOptions = computed(() => [
  { text: t('Reviews.product.view.filters.timeframeOptions.all'), value: TimeFrameOptionsEnum.NONE },
  { text: t('Reviews.product.view.filters.timeframeOptions.optionOne'), value: TimeFrameOptionsEnum.LAST_30_DAYS },
  { text: t('Reviews.product.view.filters.timeframeOptions.optionTwo'), value: TimeFrameOptionsEnum.LAST_3_MONTH },
  { text: t('Reviews.product.view.filters.timeframeOptions.optionThree'), value: TimeFrameOptionsEnum.LAST_6_MONTH },
  { text: t('Reviews.product.view.filters.timeframeOptions.optionFour'), value: TimeFrameOptionsEnum.LAST_12_MONTH },
  { text: t('Reviews.product.view.filters.timeframeOptions.optionFive'), value: TimeFrameOptionsEnum.OLDER_THAN_12_MONTH },
])

const ratingOptions = [
  { text: '1.0', value: RecommendationRatingKeyEnum.Bad, renderType: 'ratingStars' },
  { text: '2.0', value: RecommendationRatingKeyEnum.Worse, renderType: 'ratingStars' },
  { text: '3.0', value: RecommendationRatingKeyEnum.Good, renderType: 'ratingStars' },
  { text: '4.0', value: RecommendationRatingKeyEnum.Better, renderType: 'ratingStars' },
  { text: '5.0', value: RecommendationRatingKeyEnum.Best, renderType: 'ratingStars' },
]

const stateOptions = computed(() => [
  {
    text: t('Reviews.product.view.table.statusLabel.published'),
    value: SurveyResponsesStateEnum.Published,
    renderType: 'stateBadges',
    responseState: SurveyResponseState.Published,
  },
  {
    text: t('Reviews.product.view.table.statusLabel.drafted'),
    value: SurveyResponsesStateEnum.Drafted,
    renderType: 'stateBadges',
    responseState: SurveyResponseState.Drafted,
  },
  {
    text: t('Reviews.product.view.table.statusLabel.reDrafted'),
    value: SurveyResponsesStateEnum.InRevision,
    renderType: 'stateBadges',
    responseState: SurveyResponseState.ReDrafted,
  },
  {
    text: t('Reviews.product.view.table.statusLabel.stale'),
    value: SurveyResponsesStateEnum.Stale,
    renderType: 'stateBadges',
    responseState: SurveyResponseState.Stale,
  },
  {
    text: t('Reviews.product.view.table.statusLabel.rejected'),
    value: SurveyResponsesStateEnum.Rejected,
    renderType: 'stateBadges',
    responseState: SurveyResponseState.Rejected,
  },
  {
    text: t('Reviews.product.view.table.statusLabel.proposed'),
    value: SurveyResponsesStateEnum.Proposed,
    renderType: 'stateBadges',
    responseState: SurveyResponseState.Proposed,
  },
])

const interactionOptions = computed(() => [
  {
    text: t('Reviews.product.view.filters.interactionOptions.all'),
    value: ReviewInteractionType.NONE,
    renderType: 'interactionStatus',
    interactionState: null,
  },
  {
    text: t('Reviews.product.view.filters.interactionOptions.optionOne'),
    value: ReviewInteractionType.REMINDER_CHECK,
    renderType: 'interactionStatus',
    interactionState: ReviewInteractionType.REMINDER_CHECK,
  },
  {
    text: t('Reviews.product.view.filters.interactionOptions.optionTwo'),
    value: ReviewInteractionType.REMINDER,
    renderType: 'interactionStatus',
    interactionState: ReviewInteractionType.REMINDER,
  },
  {
    text: t('Reviews.product.view.filters.interactionOptions.optionThree'),
    value: ReviewInteractionType.REPLY_CHECK,
    renderType: 'interactionStatus',
    interactionState: ReviewInteractionType.REPLY_CHECK,
  },
  {
    text: t('Reviews.product.view.filters.interactionOptions.optionFour'),
    value: ReviewInteractionType.REPLY,
    renderType: 'interactionStatus',
    interactionState: ReviewInteractionType.REPLY,
  },
  {
    text: t('Reviews.product.view.filters.interactionOptions.optionFive'),
    value: ReviewInteractionType.REPLY_OUTDATED,
    renderType: 'interactionStatus',
    interactionState: ReviewInteractionType.REPLY_OUTDATED,
  },
])

const purposesOptions = computed<reviewsFilterMultiSelectOption[]>(() =>
  (props.purposes ?? []).map((purpose) => ({
    text: purpose.name ?? '',
    value: purpose.key,
  })),
)
</script>

<style scoped>
@reference '@ramp106/omrjs-config-tailwind/styles/index.css';

.filter-selected {
  @apply border-strong text-subtle;
}
.filter-default {
  @apply border-main text-subtlest disabled:border-disabled disabled:text-disabled;
}
</style>
